/**
 * screens/modals/ResolveVarianceModal
 * ──────────────────────────────────────────────────────────────────────────
 * Route a variance, or waive it.
 *
 * There is no third option, and that is the whole of invariant 3: no variance
 * is ever absorbed. Either it becomes someone's consequence — a shortfall to
 * Payroll, a surplus to Billing, per the org's routing — or it is written off
 * WITH A REASON that stays on the record.
 *
 * A waive without a reason is refused here and refused again by the API
 * (ERR_VARIANCE_REASON_REQUIRED). Routing needs no reason, because the routing
 * rule already says where it goes.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { Modal, Textarea } from '../../components';
import { neutral } from '../../theme/colors';
import { radii } from '../../theme/radii';
import { sans, size as fontSize, leading } from '../../theme/typography';
import { resolveVariance } from '../../services/reconciliation/reconciliation.service';
import { useShell } from '../../context/ShellContext';
import { useModalSubmit } from './useModalSubmit';

export interface ResolveVarianceModalProps {
  reconId: string;
  action: 'route' | 'waive';
  /** The API's own consequence sentence, shown so the choice is informed. */
  consequence: string;
}

export function ResolveVarianceModal({
  reconId,
  action,
  consequence,
}: ResolveVarianceModalProps) {
  const { closeModal } = useShell();
  const [reason, setReason] = useState('');

  const isWaive = action === 'waive';

  const { submitting, run } = useModalSubmit({
    validate: () =>
      isWaive && !reason.trim()
        ? 'Waiving a variance requires a reason. It stays on the record.'
        : null,
    submit: () => resolveVariance(reconId, action, isWaive ? reason.trim() : undefined),
    title: isWaive ? 'Variance waived' : 'Variance routed',
    fallback: isWaive
      ? 'The reason has been recorded against the count.'
      : 'The consequence has been queued for its destination.',
  });

  return (
    <Modal
      visible
      title={isWaive ? 'Waive this variance' : 'Route this variance'}
      hint={
        isWaive
          ? 'Nothing is posted out. The reason is what the record will show.'
          : 'The difference becomes a consequence in Payroll or Billing.'
      }
      onClose={closeModal}
      primaryLabel={isWaive ? 'Waive it' : 'Route it'}
      onPrimary={run}
      primaryLoading={submitting}
    >
      <View style={styles.box}>
        <Text style={styles.boxLabel}>What happens</Text>
        <Text style={styles.boxValue}>{consequence}</Text>
      </View>

      {isWaive && (
        <Textarea
          label="Reason"
          required
          hint="Why this difference will not be recovered. Kept with the count."
          value={reason}
          onChangeText={setReason}
          placeholder="Rounding on a cash sale, agreed with the store manager"
          maxLength={1000}
          rows={4}
        />
      )}
    </Modal>
  );
}

const styles = StyleSheet.create({
  box: {
    padding: 13,
    backgroundColor: neutral.surface2,
    borderWidth: 1,
    borderColor: neutral.border,
    borderRadius: radii.box,
    gap: 3,
  },
  boxLabel: { ...sans('400'), fontSize: fontSize.meta, color: neutral.grey600 },
  boxValue: {
    ...sans('500'),
    fontSize: fontSize.button,
    lineHeight: leading(fontSize.button),
    color: neutral.ink,
  },
});

export default ResolveVarianceModal;
